import { useEffect } from "react";
import type { WalkthroughFinding } from "./workspace-types";

interface UseWorkspaceKeyboardOptions {
  filteredFindings: WalkthroughFinding[];
  focusedIndex: number;
  selectedIds: Set<string>;
  isModalOpen: boolean;
  clearSelection: () => void;
  moveFocus: (direction: "next" | "prev", total: number) => void;
  toggleSelect: (id: string) => void;
}

export function useWorkspaceKeyboard({
  filteredFindings,
  focusedIndex,
  selectedIds,
  isModalOpen,
  clearSelection,
  moveFocus,
  toggleSelect,
}: UseWorkspaceKeyboardOptions) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        isModalOpen
      ) {
        return;
      }

      if (e.key === "Escape") {
        if (selectedIds.size > 0) {
          e.preventDefault();
          clearSelection();
        }
        return;
      }

      if (e.key === "j" || e.key === "ArrowDown") {
        if (filteredFindings.length === 0) return;
        e.preventDefault();
        moveFocus("next", filteredFindings.length);
      } else if (e.key === "k" || e.key === "ArrowUp") {
        if (filteredFindings.length === 0) return;
        e.preventDefault();
        moveFocus("prev", filteredFindings.length);
      } else if (
        e.key === "x" &&
        focusedIndex >= 0 &&
        focusedIndex < filteredFindings.length
      ) {
        e.preventDefault();
        const f = filteredFindings[focusedIndex];
        if (f) toggleSelect(f.id);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    filteredFindings,
    focusedIndex,
    selectedIds,
    isModalOpen,
    clearSelection,
    moveFocus,
    toggleSelect,
  ]);
}
